/**
 * Staff-user gamification section (level, points, badge).
 */
import * as React from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { fetchGamificationForUser } from '@/services/gamification.service';
import { GamificationLevelBadgeImage } from '@/components/gamification/GamificationLevelBadgeImage';
import {
  gamificationErrorMessage,
  hasNextLevelProgress,
  resolveLevelDisplayLabel,
  shouldShowLevelBadge,
  xpProgressPercent,
} from '@/components/gamification/gamification-utils';

/** Props for {@link StaffUserGamificationSection}. */
export interface StaffUserGamificationSectionProps {
  userId: string;
}

/** Staff profile block: level badge, XP, and progress toward the next level. */
export const StaffUserGamificationSection: React.FC<StaffUserGamificationSectionProps> = ({
  userId,
}) => {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['gamification', 'user', userId],
    queryFn: () => fetchGamificationForUser(userId),
    enabled: Boolean(userId.trim()),
  });

  /** Retry the gamification fetch. */
  function handleRetry(): void {
    void refetch();
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Chargement de la progression…
      </div>
    );
  }

  if (isError) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    const message = gamificationErrorMessage(status);

    return (
      <div className="space-y-2 py-1">
        <p className="text-sm text-muted-foreground">{message}</p>
        <button
          type="button"
          onClick={handleRetry}
          className="text-sm font-medium text-[var(--color-1-nuance-1)] hover:underline"
        >
          Réessayer
        </button>
      </div>
    );
  }

  if (!data) {
    return <p className="text-sm text-muted-foreground">Aucune donnée de progression.</p>;
  }

  const {
    currentNiveau,
    nextNiveau,
    totalXp,
    xpToNextLevel,
    xpRangeToNext,
    xpInCurrentLevel,
    levelBadge,
  } = data;

  const showBadgeVisual = shouldShowLevelBadge({
    levelBadge,
    levelNumber: currentNiveau?.numero,
    levelIcon: currentNiveau?.icone,
  });
  const tierLabel = resolveLevelDisplayLabel({
    badgeLabel: levelBadge?.displayLabel,
    levelNumber: currentNiveau?.numero,
    levelName: currentNiveau?.nom,
  });
  const showProgress = hasNextLevelProgress({
    nextNiveau,
    xpToNextLevel,
    xpRangeToNext,
  });

  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
      {showBadgeVisual ? (
        <GamificationLevelBadgeImage
          size={88}
          levelBadge={levelBadge}
          levelNumber={currentNiveau?.numero}
          levelIcon={currentNiveau?.icone}
        />
      ) : null}
      <div className="flex-1 min-w-0 space-y-3">
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Badge de niveau
          </p>
          <p className="text-lg font-bold text-foreground">{tierLabel ?? '—'}</p>
          {currentNiveau?.nom ? (
            <p className="text-sm text-muted-foreground">Niveau : {currentNiveau.nom}</p>
          ) : null}
        </div>
        <div>
          <p className="text-sm font-semibold text-[var(--color-1-nuance-1)]">{totalXp} XP</p>
          {showProgress && xpRangeToNext != null ? (
            <>
              <div className="mt-2 h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-[var(--color-1-nuance-1)] transition-[width]"
                  style={{
                    width: `${xpProgressPercent(xpInCurrentLevel, xpRangeToNext)}%`,
                  }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {totalXp} / {nextNiveau.xpRequis} XP vers {nextNiveau.nom}
              </p>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};
